import { useCallback, useEffect, useState } from 'react'
import { ApiClientError } from '@/lib/api/client'
import {
  consumeThankYouResume,
  hasResumablePaidWidgetFlow,
  readThankYouResumeToken,
  readThankYouResumeSnapshot,
  saveThankYouResumeSnapshot,
  stripThankYouResumeTokenFromUrl,
  type ThankYouResumePayload,
} from '@/lib/landing-thank-you'

type ThankYouResumePhase = 'loading' | 'ready' | 'empty' | 'error'

type UseLandingThankYouResumeResult = {
  retry: () => void
  phase: ThankYouResumePhase
  snapshot: ThankYouResumePayload | null
  errorMessage: string | null
}

type ConsumeResult = {
  campaignId: number
  token: string
  snapshot: ThankYouResumePayload | null
  errorMessage: string | null
}

export function useLandingThankYouResume(
  campaignId: number | null,
  search: string
): UseLandingThankYouResumeResult {
  const [consumeResult, setConsumeResult] = useState<ConsumeResult | null>(null)
  const [retryKey, setRetryKey] = useState(0)
  const retry = useCallback(() => {
    setConsumeResult(null)
    setRetryKey((key) => key + 1)
  }, [])
  const resumeToken = readThankYouResumeToken(search)

  useEffect(() => {
    if (campaignId == null || campaignId <= 0 || !resumeToken) {
      return
    }

    let cancelled = false

    void consumeThankYouResume(campaignId, resumeToken)
      .then((payload) => {
        if (cancelled) {
          return
        }
        if (payload.campaign_id !== campaignId) {
          stripThankYouResumeTokenFromUrl()
          setConsumeResult({
            campaignId,
            token: resumeToken,
            snapshot: null,
            errorMessage: 'Não encontramos uma compra para continuar nesta página.',
          })
          return
        }
        saveThankYouResumeSnapshot(payload)
        stripThankYouResumeTokenFromUrl()
        setConsumeResult({
          campaignId,
          token: resumeToken,
          snapshot: payload,
          errorMessage: null,
        })
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return
        }
        const invalidToken =
          error instanceof ApiClientError &&
          [400, 401, 403, 404, 410, 422].includes(error.status)
        if (invalidToken) stripThankYouResumeTokenFromUrl()
        const message = invalidToken
          ? 'Não encontramos uma compra para continuar nesta página.'
          : 'Não foi possível confirmar seu pagamento agora. Tente novamente em instantes.'
        setConsumeResult({
          campaignId,
          token: resumeToken,
          snapshot: null,
          errorMessage: message,
        })
      })

    return () => {
      cancelled = true
    }
  }, [campaignId, resumeToken, retryKey])

  if (campaignId == null || campaignId <= 0) {
    return {
      retry,
      phase: 'loading',
      snapshot: null,
      errorMessage: null,
    }
  }

  if (!resumeToken) {
    if (
      consumeResult &&
      consumeResult.campaignId === campaignId &&
      consumeResult.snapshot &&
      !consumeResult.errorMessage
    ) {
      return {
        retry,
        phase: 'ready',
        snapshot: consumeResult.snapshot,
        errorMessage: null,
      }
    }

    const savedSnapshot = readThankYouResumeSnapshot(campaignId)
    return {
      retry,
      phase: savedSnapshot || hasResumablePaidWidgetFlow(campaignId) ? 'ready' : 'empty',
      snapshot: savedSnapshot,
      errorMessage: null,
    }
  }

  if (
    consumeResult &&
    consumeResult.campaignId === campaignId &&
    consumeResult.token === resumeToken
  ) {
    if (consumeResult.errorMessage) {
      return {
        retry,
        phase: 'error',
        snapshot: null,
        errorMessage: consumeResult.errorMessage,
      }
    }

    return {
      retry,
      phase: 'ready',
      snapshot: consumeResult.snapshot,
      errorMessage: null,
    }
  }

  return {
    retry,
    phase: 'loading',
    snapshot: null,
    errorMessage: null,
  }
}
